import {
  useDeletePresentationMutation,
  useGetDemoQuery,
  useLazyGetPresentationsByDemoQuery,
  usePostEventMutation,
} from '@carboncopyco/data';
import {
  ArchiveIcon,
  DeleteIcon,
  MoreVerticalOutlineIcon,
  NotesIcon,
  PlayIcon,
  ShareOutlineIcon,
  ShowOutlineIcon,
} from '@carboncopyco/icons';
import {
  type EnhancedPresentation,
  EventAction,
  EventTarget,
} from '@carboncopyco/types';
import {
  Button,
  ColorHex,
  ConfirmAlert,
  Dropdown,
  DropdownItem,
  InlineLink,
  Loading,
  TextCaption,
  Tooltip,
  TooltipBubble,
  notify,
} from '@carboncopyco/ui';
import {
  AnalyticsTrackEventName,
  Debug,
  isDesktop,
  useAnalytics,
} from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLinksPermissions } from '../../../utils/hooks/useLinksPermissions';
import { usePresentationPreview } from '../../usePresentationPreview';
import { useDemoPageProps } from '../DemoPageContext';
import { SharePresentationFlow } from './SharePresentationFlow';
import SpeakerNotesModal from './SpeakerNotesModal';

interface PresentationRowButtonsProps {
  presentation: EnhancedPresentation;
}

const debug = Debug.extend('presentation-row-buttons');

export const PresentationRowButtons = ({
  presentation,
}: PresentationRowButtonsProps) => {
  const archivePresentationRef = useRef<HTMLDivElement>(null);
  const { id, variables, demoId } = presentation;
  const { trackEvent } = useAnalytics();
  const {
    goToDemoPreview,
    goToPresentation,
    organizationId,
    rootPath,
    onLearnMore,
    onViewShares,
    workspaceId,
  } = useDemoPageProps();
  const { data: demo } = useGetDemoQuery({
    organizationId,
    workspaceId,
    id: String(demoId),
  });
  const { getIsDemoShareable } = useLinksPermissions();
  const [postEvent] = usePostEventMutation();
  const [archivePresentation] = useDeletePresentationMutation();
  const [triggerGetPresentationsByDemo] = useLazyGetPresentationsByDemoQuery();
  const [
    showArchiveModal,
    { setTrue: setTruesShowArchiveModal, setFalse: setFalseShowArchiveModal },
  ] = useBoolean();
  const [
    showShareModal,
    { setTrue: setTruesShowShareModal, setFalse: setFalseShowShareModal },
  ] = useBoolean();
  const [isPresenting, setIsPresenting] = useBoolean(false);
  const location = 'demo_info_page_presentations_tab_table_side_actions';
  const [
    showPresentationNotes,
    {
      setTrue: setTrueShowPresentationNotes,
      setFalse: setFalseShowPresentationNotes,
    },
  ] = useBoolean(false);
  const navigate = useNavigate();

  const onPlayClick = async () => {
    setIsPresenting.setTrue();
    trackEvent(AnalyticsTrackEventName.DEMO_CLICK_PRESENT, {
      location,
    });
    postEvent({
      organizationId,
      presentationId: String(presentation.id),
      action: EventAction.STARTED,
      target: EventTarget.PRESENTATION,
    })
      .unwrap()
      .then(() => {
        // TODO: remove this after we implement notification service
        // WHY it's here: because the state of presentation is not updated immediately after the event is posted (RND-9651)
        setTimeout(() => {
          triggerGetPresentationsByDemo({
            organizationId,
            workspaceId,
            demoId: String(demoId),
            clearCache: true,
          });
        }, 15000);
      })
      .catch((error) => debug(error));

    try {
      await goToPresentation(presentation);
    } finally {
      setIsPresenting.setFalse();
    }
  };

  const onPresentationNotesLearnMore = () => {
    setFalseShowShareModal();
    if (onLearnMore) {
      onLearnMore();
      return;
    }
    navigate(`${rootPath}`);
  };

  const handlePreview = usePresentationPreview({
    presentation,
    organizationId,
    goToDemoPreview,
    from: 'presentation',
  });

  return (
    <div className="flex items-center gap-3">
      <TooltipBubble
        placement="top"
        title="Preview"
      >
        <Button
          aria-label="preview"
          dark
          icon={<ShowOutlineIcon color={ColorHex.natural600} />}
          onClick={() => {
            trackEvent(AnalyticsTrackEventName.DEMO_CLICK_PREVIEW, {
              location,
            });
            handlePreview();
          }}
        />
      </TooltipBubble>
      <TooltipBubble
        placement="top"
        title="Present now"
      >
        <Button
          aria-label="Present button"
          disabled={isPresenting}
          dark
          icon={
            isPresenting && isDesktop() ? (
              <Loading />
            ) : (
              <PlayIcon color={ColorHex.natural600} />
            )
          }
          onClick={onPlayClick}
        />
      </TooltipBubble>
      <Dropdown
        width={173}
        asChild
        side="left"
        triggerElement={
          <div>
            <TooltipBubble
              placement="top"
              title="More actions"
            >
              <Button
                aria-label="actions"
                dark
                icon={<MoreVerticalOutlineIcon color={ColorHex.natural600} />}
              />
            </TooltipBubble>
          </div>
        }
      >
        {getIsDemoShareable(demo) && (
          <DropdownItem
            iconLeft={<ShareOutlineIcon size={18} />}
            label="Share"
            onClick={() => {
              trackEvent(
                AnalyticsTrackEventName.DEMO_CLICK_SHARE_PRESENTATION,
                {
                  location,
                }
              );
              setTruesShowShareModal();
            }}
          />
        )}
        {!!presentation?.notes?.length && (
          <DropdownItem
            iconLeft={<NotesIcon size={18} />}
            label="View notes"
            onClick={setTrueShowPresentationNotes}
          />
        )}
        <div ref={archivePresentationRef}>
          <DropdownItem
            disabled={!!presentation.totalShares}
            iconLeft={<ArchiveIcon size={18} />}
            label="Archive"
            onClick={() => {
              trackEvent(
                AnalyticsTrackEventName.DEMO_CLICK_ARCHIVE_PRESENTATION,
                {
                  location,
                }
              );
              setTruesShowArchiveModal();
            }}
          />
        </div>
        {!!presentation.totalShares && (
          <Tooltip
            anchorRef={archivePresentationRef}
            placement="bottom"
            offset={[0, 15]}
          >
            <div className="flex flex-col items-start p-2.5 w-ds-46">
              <TextCaption
                label="To archive this presentation, please deactivate all of its shared links first."
                color="text-ds-natural-600"
              />
              <InlineLink
                as={Link}
                to={`${rootPath}/links?query=&sourcePresentationIds=${presentation.id}`}
                label="View shares"
              />
            </div>
          </Tooltip>
        )}
      </Dropdown>
      {showPresentationNotes && (
        <SpeakerNotesModal
          presentation={presentation}
          open={showPresentationNotes}
          onLearnMore={onPresentationNotesLearnMore}
          onClose={setFalseShowPresentationNotes}
        />
      )}
      {showArchiveModal && (
        <ConfirmAlert
          title="Archive presentation"
          description="Archiving will remove this presentation from your environment"
          containerClass="w-ds-normal-modal"
          danger
          yesIcon={<DeleteIcon />}
          yesLabel="Archive"
          show={showArchiveModal}
          onClose={() => {
            trackEvent(
              AnalyticsTrackEventName.DEMO_CLICK_ARCHIVE_PRESENTATION_CANCEL,
              {
                location: 'demo_info_page_archive_presentation_modal',
              }
            );
            setFalseShowArchiveModal();
          }}
          onConfirm={() => {
            trackEvent(
              AnalyticsTrackEventName.DEMO_CLICK_ARCHIVE_PRESENTATION_APPROVE,
              {
                location: 'demo_info_page_archive_presentation_modal',
              }
            );
            archivePresentation({
              organizationId,
              id: String(presentation.id),
            })
              .unwrap()
              .then(() => notify.success('Archived presentation successfully'))
              .catch(() =>
                notify.error('There was an error archiving this presentation')
              )
              .finally(setFalseShowArchiveModal);
          }}
        />
      )}
      {showShareModal && (
        <SharePresentationFlow
          variables={variables}
          onClose={setFalseShowShareModal}
          id={Number(id)}
          onViewShares={onViewShares}
        />
      )}
    </div>
  );
};
